import {
  Award,
  Globe,
  Heart,
  Lightbulb,
  Shield,
  ShieldCheck,
  Target,
  TrendingUp,
  Users,
  Zap,
} from "lucide-react";
import { useEffect, useState } from "react";

import AppNavbar from "@/components/common/AppNavbar";
import Breadcrumbs from "@/components/common/Breadcrumbs";
import { fetchAboutUsSections } from "@/services/landing/aboutUsService";
import { fetchHeroBackgrounds } from "@/services/landing/heroBackgroundService";

interface AboutUsSection {
  color: string;
  content: string;
  icon: string;
  id: string;
  imageAlt: string;
  imageUrl: string;
  isActive: boolean;
  sectionType: string;
  title: string;
}

export default function WhoWeAre() {
  const [sections, setSections] = useState<AboutUsSection[]>([]);
  const [loading, setLoading] = useState(true);
  const [heroBackground, setHeroBackground] = useState<null | {
    imageAlt: string;
    imageUrl: string;
  }>(null);
  const [heroLoading, setHeroLoading] = useState(true);

  useEffect(() => {
    const loadData = async () => {
      try {
        const [sectionsData, heroData] = await Promise.all([
          fetchAboutUsSections(),
          fetchHeroBackgrounds("about"),
        ]);
        setSections(sectionsData);
        setHeroBackground(heroData);
      } catch (error) {
        console.error("Failed to fetch About Us data:", error);
      } finally {
        setLoading(false);
        setHeroLoading(false);
      }
    };

    loadData();
  }, []);

  // Helper function to get icon component by name
  const getIconComponent = (iconName: string) => {
    const icons: Record<string, React.ReactElement> = {
      award: <Award className="text-warning" size={48} />,
      lightbulb: <Lightbulb className="text-primary" size={48} />,
      shieldcheck: <ShieldCheck className="text-primary" size={48} />,
      target: <Target className="text-accent" size={48} />,
      trendingup: <TrendingUp className="text-success" size={48} />,
    };
    return (
      icons[iconName.toLowerCase()] || (
        <Lightbulb className="text-primary" size={48} />
      )
    );
  };

  // Get specific sections by type
  const storySection = sections.find((s) => s.sectionType === "story");
  const missionSection = sections.find((s) => s.sectionType === "mission");
  const visionSection = sections.find((s) => s.sectionType === "vision");

  const values = [
    {
      description: "Every decision we make puts our customers at the center",
      icon: <Heart className="text-primary" size={32} />,
      title: "Customer First",
    },
    {
      description:
        "We constantly push boundaries to improve mobility solutions",
      icon: <Zap className="text-warning" size={32} />,
      title: "Innovation",
    },
    {
      description: "Building trust through transparency and safety excellence",
      icon: <Shield className="text-success" size={32} />,
      title: "Safety & Trust",
    },
    {
      description: "Creating a greener future through sustainable transport",
      icon: <Globe className="text-info" size={32} />,
      title: "Sustainability",
    },
  ];

  const stats = [
    { label: "Founded", number: "2019" },
    { label: "Rides Completed", number: "50M+" },
    { label: "Cities Served", number: "15" },
    { label: "Driver Partners", number: "100K+" },
  ];

  return (
    <>
      <AppNavbar />

      <div className="min-h-screen bg-base-200">
        <Breadcrumbs />

        {/* Hero Section */}
        <section className="relative -mt-16 overflow-hidden pt-16">
          <div className="absolute inset-0 opacity-20">
            {heroLoading ? (
              <div className="relative h-full w-full overflow-hidden bg-base-200">
                <div
                  className="animate-shimmer absolute inset-0 bg-gradient-to-r from-base-200 via-base-100 to-base-200"
                  style={{ backgroundSize: "200% 100%" }}
                />
              </div>
            ) : heroBackground?.imageUrl ? (
              <img
                alt={heroBackground?.imageAlt || "Hero background"}
                className="h-full w-full object-cover"
                src={heroBackground.imageUrl}
              />
            ) : null}
          </div>

          <div className="relative mx-auto max-w-7xl px-4 py-24">
            <div className="max-w-4xl">
              <h1 className="mb-6 text-5xl font-bold leading-tight lg:text-6xl">
                We are building the future of{" "}
                <span className="text-primary">mobility in Africa</span>
              </h1>

              <p className="mb-8 text-xl leading-relaxed opacity-90">
                Hareeng is more than a mobility company. We are a platform that
                connects people, powers economies, and transforms cities across
                Africa and beyond.
              </p>

              <div className="flex flex-col gap-4 sm:flex-row">
                <a className="btn btn-primary btn-lg" href="#mission">
                  Join Our Mission
                </a>
                <a className="btn btn-outline btn-lg" href="#impact">
                  Our Impact
                </a>
              </div>
            </div>
          </div>
        </section>

        {/* Stats */}
        {/* <section className="bg-base-100 py-16">
          <div className="mx-auto max-w-7xl px-4">
            <div className="grid gap-8 md:grid-cols-4">
              {stats.map((stat, index) => (
                <div className="text-center" key={index}>
                  <div className="mb-2 text-4xl font-bold text-primary">
                    {stat.number}
                  </div>
                  <div className="text-base-content/70">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>
        </section> */}

        {/* Our Story */}
        <section className="py-16">
          <div className="mx-auto max-w-7xl px-4">
            {loading ? (
              <div className="text-center">
                <span className="loading loading-spinner loading-lg"></span>
              </div>
            ) : storySection ? (
              <div className="grid items-center gap-12 lg:grid-cols-2">
                <div>
                  <h2 className="mb-6 text-4xl font-bold">
                    {storySection.title}
                  </h2>
                  <div className="space-y-4 whitespace-pre-line text-lg">
                    {storySection.content}
                  </div>
                </div>

                <div className="relative">
                  {storySection.imageUrl && (
                    <img
                      alt={storySection.imageAlt || storySection.title}
                      className="rounded-2xl shadow-2xl"
                      src={storySection.imageUrl}
                    />
                  )}
                </div>
              </div>
            ) : (
              <div className="grid items-center gap-12 lg:grid-cols-2">
                <div>
                  <h2 className="mb-6 text-4xl font-bold">Our Story</h2>
                  <div className="space-y-4 text-lg">
                    <p>
                      Founded in 2025 by Bokang Mothomotsoana in Maseru,
                      Lesotho, Hareeng started with a simple vision: to make
                      transportation accessible, affordable, and sustainable for
                      everyone in Africa.
                    </p>
                    <p>
                      What began as a ride-hailing service has evolved into a
                      comprehensive mobility ecosystem, including food delivery,
                      electric scooters, and logistics solutions.
                    </p>
                    <p>
                      Today, we are proud to serve millions of users across many
                      cities, supporting thousands of driver-partners and
                      restaurant owners while contributing to economic growth in
                      our communities.
                    </p>
                  </div>
                </div>

                <div className="relative">
                  <img
                    alt="City transportation"
                    className="rounded-2xl shadow-2xl"
                    src="https://images.unsplash.com/photo-1557804506-669a67965ba0?w=600&h=400&fit=crop&crop=center"
                  />
                </div>
              </div>
            )}
          </div>
        </section>

        {/* Our Values */}
        <section className="bg-base-100 py-16">
          <div className="mx-auto max-w-7xl px-4">
            <div className="mb-12 text-center">
              <h2 className="mb-4 text-4xl font-bold">Our Values</h2>
              <p className="text-xl text-base-content/70">
                The principles that guide everything we do
              </p>
            </div>

            <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-4">
              {values.map((value, index) => (
                <div className="card bg-base-200 shadow-lg" key={index}>
                  <div className="card-body text-center">
                    <div className="mx-auto mb-4">{value.icon}</div>
                    <h3 className="card-title justify-center">{value.title}</h3>
                    <p className="text-base-content/70">{value.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Mission & Vision */}
        <section className="py-16" id="mission">
          <div className="mx-auto max-w-7xl px-4">
            {loading ? (
              <div className="text-center">
                <span className="loading loading-spinner loading-lg"></span>
              </div>
            ) : (
              <div className="grid gap-12 lg:grid-cols-2">
                {/* Mission */}
                {missionSection ? (
                  <div className="text-center lg:text-left">
                    <div className="mb-6 flex justify-center lg:justify-start">
                      {getIconComponent(missionSection.icon)}
                    </div>
                    <h3 className="mb-4 text-3xl font-bold">
                      {missionSection.title}
                    </h3>
                    <p className="whitespace-pre-line text-xl opacity-90">
                      {missionSection.content}
                    </p>
                  </div>
                ) : (
                  <div className="text-center lg:text-left">
                    <div className="mb-6 flex justify-center lg:justify-start">
                      <Target className="text-accent" size={48} />
                    </div>
                    <h3 className="mb-4 text-3xl font-bold">Our Mission</h3>
                    <p className="text-xl opacity-90">
                      To provide safe, reliable, and sustainable mobility
                      solutions that empower communities, drive economic growth,
                      and enhance the quality of life for people across Africa.
                    </p>
                  </div>
                )}

                {/* Vision */}
                {visionSection ? (
                  <div className="text-center lg:text-left">
                    <div className="mb-6 flex justify-center lg:justify-start">
                      {getIconComponent(visionSection.icon)}
                    </div>
                    <h3 className="mb-4 text-3xl font-bold">
                      {visionSection.title}
                    </h3>
                    <p className="whitespace-pre-line text-xl opacity-90">
                      {visionSection.content}
                    </p>
                  </div>
                ) : (
                  <div className="text-center lg:text-left">
                    <div className="mb-6 flex justify-center lg:justify-start">
                      <Award className="text-warning" size={48} />
                    </div>
                    <h3 className="mb-4 text-3xl font-bold">Our Vision</h3>
                    <p className="text-xl opacity-90">
                      To be the leading mobility platform in Africa,
                      transforming the way people move, work, and live while
                      fostering sustainable urban development.
                    </p>
                  </div>
                )}
              </div>
            )}
          </div>
        </section>

        {/* Impact */}
        <section className="py-16" id="impact">
          <div className="mx-auto max-w-7xl px-4">
            <div className="mb-12 text-center">
              <h2 className="mb-4 text-4xl font-bold">Our Impact</h2>
              <p className="text-xl text-base-content/70">
                Creating positive change in communities across Africa
              </p>
            </div>

            <div className="grid gap-8 md:grid-cols-3">
              <div className="card border border-success/20 bg-success/10">
                <div className="card-body text-center">
                  <TrendingUp className="mx-auto mb-4 text-success" size={48} />
                  <h3 className="card-title justify-center text-success">
                    Economic Growth
                  </h3>
                  <p>
                    Created many income opportunities for drivers, delivery
                    partners, and restaurant owners.
                  </p>
                </div>
              </div>

              <div className="card border border-info/20 bg-info/10">
                <div className="card-body text-center">
                  <Globe className="mx-auto mb-4 text-info" size={48} />
                  <h3 className="card-title justify-center text-info">
                    Environmental
                  </h3>
                  <p>
                    Reduced carbon emissions by promoting shared mobility and
                    electric vehicle adoption.
                  </p>
                </div>
              </div>

              <div className="card border border-warning/20 bg-warning/10">
                <div className="card-body text-center">
                  <Users className="mx-auto mb-4 text-warning" size={48} />
                  <h3 className="card-title justify-center text-warning">
                    Social
                  </h3>
                  <p>
                    Improve access to transportation in underserved communities
                    across African countries.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>
      </div>
    </>
  );
}
